<template>
  <div ref="popupRef" :class="style.listPopup" role="menu">
    <div
      v-for="(item, index) in localListAction"
      :key="getItemKey(item, index)"
      :class="[
        style.listItem,
        isComplex(item) ? style.draggableItem : '',
        draggedIndex === index ? style.draggedItem : '',
        dragOverIndex === index ? style.dragOverItem : ''
      ]"
      @dragover.prevent="onDragOver(index, item)"
      @drop.prevent="onDrop(index, item)"
      @dragleave="onDragLeave(index)"
    >
      <div v-if="isButton(item)" :class="getButtonWrapperClasses(index)">
        <slot
          name="button"
          :item="item"
          :index="index"
          :select="() => onActionClick(item, index)"
        >
          <component
            :is="resolveButtonComponent(item)"
            v-bind="getButtonProps(item)"
            @click.stop="onActionClick(item, index)"
          />
        </slot>
      </div>

      <DropdownTab
        v-else-if="isTab(item)"
        :text="item.text"
        :icon="item.icon || ''"
        :index="index"
        :maxSize="localListAction.length"
        :disabled="item.disabled"
        :active="item?.active"
        @click.stop="onActionClick(item, index)"
      />

      <DropdownComplex
        v-else-if="isComplex(item)"
        :text="item.text"
        :index="index"
        :maxSize="localListAction.length"
        :hideButton="item?.hideButton"
        :draggable="true"
        :disabled="item.disabled"
        :actionHandler="item?.actionHandler"
        :icon="item?.icon"
        :active="item?.active"
        size="S"
        @click.stop="onActionClick(item, index)"
        @drag-start="onDragStart(index, item, $event)"
        @drag-end="onDragEnd"
      />

      <DropdownHeader
        v-else-if="isHeader(item)"
        :text="item.text"
        :icon="item.icon"
        :index="index"
        :maxSize="localListAction.length"
        @click.stop="onActionClick(item, index)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import style from "./ListPopup.module.scss";
import {defineComponent, PropType, toRaw, type Component} from "vue";

import DropdownTab from "@/elements/UI/list/dropdown/Tab/DropdownTab.vue";
import DropdownHeader from "@/elements/UI/list/dropdown/Header/DropdownHeader.vue";
import DropdownComplex from "@/elements/UI/list/dropdown/Complex/DropdownComplex.vue";
import GhostButton from "@/elements/UI/button/ButtonStyle/Default/ghost/GhostButton.vue";
import dropdownStyle from "@/elements/UI/list/dropdown/Dropdown.module.scss";

export default defineComponent({
  name: "ListPopup",

  components: {
    DropdownComplex,
    DropdownHeader,
    DropdownTab,
    GhostButton
  },

  emits: {
    close: (): boolean => true,

    change: (list: ListActionItem[]): boolean => {
      return Array.isArray(list);
    },

    reorder: (payload: ListReorderPayload): boolean => {
      return (
        typeof payload.fromIndex === "number" &&
        typeof payload.toIndex === "number" &&
        Array.isArray(payload.list)
      );
    },

    select: (payload: ListSelectPayload): boolean => {
      return !!payload.item && typeof payload.index === "number";
    }
  },

  props: {
    listAction: {
      type: Array as PropType<ListActionItem[]>,
      default: (): ListActionItem[] => []
    },

    closeOnSelect: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      style,
      localListAction: [] as ListActionItem[],
      draggedIndex: null as number | null,
      dragOverIndex: null as number | null,
      wasDragging: false
    };
  },

  mounted() {
    this.localListAction = [...this.listAction];
  },

  methods: {
    resolveButtonComponent(item: ListActionItem): Component {
      return item.buttonComponent ? toRaw(item.buttonComponent) : GhostButton;
    },

    getButtonProps(item: ListActionItem): Record<string, unknown> {
      const buttonProps = item.buttonProps || {};

      return {
        size: "S",
        type: "button",
        ...buttonProps,
        text: buttonProps.text ?? item.text ?? "",
        icon: buttonProps.icon ?? item.icon ?? "",
        active: buttonProps.active ?? item.active ?? false,
        disabled: item.disabled ?? false
      };
    },

    getButtonWrapperClasses(index: number): string[] {
      return [
        dropdownStyle.dropdown,
        dropdownStyle.sizeS,
        this.getPositionClass(index)
      ];
    },

    getPositionClass(index: number): string {
      const maxSize = this.localListAction.length;

      if (maxSize <= 1) {
        return dropdownStyle.single;
      }

      if (index === 0) {
        return dropdownStyle.first;
      }

      if (index === maxSize - 1) {
        return dropdownStyle.last;
      }

      return dropdownStyle.middle;
    },

    getItemKey(item: ListActionItem, index: number): string {
      if (item.id !== undefined) {
        return String(item.id);
      }

      return `${item.text}-${index}`;
    },

    onActionClick(item: ListActionItem, index: number): void {
      if (item.disabled) {
        return;
      }

      if (this.wasDragging) {
        return;
      }

      this.$emit("select", {
        item,
        index
      });

      item.handler?.();

      if (this.closeOnSelect) {
        this.$emit("close");
      }
    },

    onDragStart(index: number, item: ListActionItem, event: DragEvent): void {
      if (!this.isComplex(item) || item.disabled) {
        event.preventDefault();
        return;
      }

      this.draggedIndex = index;
      this.wasDragging = true;

      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.dropEffect = "move";
        event.dataTransfer.setData("text/plain", String(index));
      }
    },

    onDragOver(index: number, item: ListActionItem): void {
      if (!this.isComplex(item) || item.disabled) {
        this.dragOverIndex = null;
        return;
      }

      if (this.draggedIndex === null) {
        this.dragOverIndex = null;
        return;
      }

      if (this.draggedIndex === index) {
        this.dragOverIndex = null;
        return;
      }

      this.dragOverIndex = index;
    },

    onDragLeave(index: number): void {
      if (this.dragOverIndex === index) {
        this.dragOverIndex = null;
      }
    },

    canDropTo(toIndex: number, targetItem: ListActionItem): boolean {
      if (!this.isComplex(targetItem) || targetItem.disabled) {
        return false;
      }

      if (this.draggedIndex === null) {
        return false;
      }

      if (this.draggedIndex === toIndex) {
        return false;
      }

      const sourceItem = this.localListAction[this.draggedIndex];

      if (!this.isComplex(sourceItem) || sourceItem.disabled) {
        return false;
      }

      return true;
    },

    moveItem(fromIndex: number, toIndex: number): ListActionItem[] {
      const updatedList = [...this.localListAction];
      const [movedItem] = updatedList.splice(fromIndex, 1);

      if (!movedItem) {
        return this.localListAction;
      }

      updatedList.splice(toIndex, 0, movedItem);

      return updatedList;
    },

    onDrop(toIndex: number, targetItem: ListActionItem): void {
      if (!this.canDropTo(toIndex, targetItem)) {
        this.clearDragState();
        return;
      }

      const fromIndex = this.draggedIndex as number;
      const updatedList = this.moveItem(fromIndex, toIndex);
      const movedItem = updatedList[toIndex];

      this.localListAction = updatedList;

      this.$emit("change", updatedList);
      this.$emit("reorder", {
        fromIndex,
        toIndex,
        item: movedItem,
        list: updatedList
      });

      this.clearDragState();
    },

    onDragEnd(): void {
      this.clearDragState();
    },

    clearDragState(): void {
      this.draggedIndex = null;
      this.dragOverIndex = null;

      window.setTimeout(() => {
        this.wasDragging = false;
      }, 100);
    },

    isButton(item: ListActionItem): boolean {
      return item.variant === "button";
    },
    isTab(item: ListActionItem): boolean {
      return item.variant === "tab";
    },
    isHeader(item: ListActionItem): boolean {
      return item.variant === "header";
    },
    isComplex(item: ListActionItem): boolean {
      return item.variant === "complex";
    }
  },

  watch: {
    listAction: {
      handler(newListAction: ListActionItem[]): void {
        this.localListAction = [...newListAction];
      },
      deep: true
    }
  }
});
</script>
